import { 
  collection, 
  addDoc, 
  updateDoc, 
  deleteDoc,
  doc, 
  getDocs, 
  getDoc,
  query,
  where,
  orderBy,
  Timestamp
} from 'firebase/firestore';
import { db } from './config';
import { logActivity, ACTIONS, ENTITY_TYPES } from './activityLogService';

const PO_COLLECTION = 'purchaseOrders';

/**
 * Generate next PO number
 */
const generatePONumber = async () => {
  try {
    const posSnapshot = await getDocs(collection(db, PO_COLLECTION));
    const poCount = posSnapshot.size;
    return `PO-${String(poCount + 1).padStart(3, '0')}`;
  } catch (error) {
    console.error('Error generating PO number:', error);
    return `PO-${Date.now()}`;
  }
};

/**
 * Create a new purchase order
 */
export const createPurchaseOrder = async (poData, currentUser) => {
  try {
    const poNumber = await generatePONumber();
    
    const poRef = await addDoc(collection(db, PO_COLLECTION), {
      poNumber,
      supplierId: poData.supplierId,
      supplierName: poData.supplierName,
      items: poData.items,
      totalAmount: poData.totalAmount,
      status: 'pending',
      expectedDeliveryDate: poData.expectedDeliveryDate,
      orderDate: Timestamp.now(),
      grns: [],
      notes: poData.notes || '',
      createdBy: currentUser?.uid || 'system',
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now()
    });

    await logActivity({
      userId: currentUser?.uid,
      userEmail: currentUser?.email,
      userName: currentUser?.displayName || currentUser?.email,
      action: ACTIONS.CREATED_PURCHASE_ORDER,
      entityType: ENTITY_TYPES.PURCHASE_ORDER,
      entityId: poRef.id,
      entityName: `PO #${poNumber} - ${poData.supplierName}`,
      details: { poNumber, supplierName: poData.supplierName, totalAmount: poData.totalAmount }
    });

    return { success: true, id: poRef.id, poNumber };
  } catch (error) {
    console.error('Error creating purchase order:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get all purchase orders
 */
export const getPurchaseOrders = async (filters = {}) => {
  try {
    let q = collection(db, PO_COLLECTION);
    
    const constraints = [];
    
    if (filters.status) {
      constraints.push(where('status', '==', filters.status));
    }
    
    if (filters.supplierId) {
      constraints.push(where('supplierId', '==', filters.supplierId));
    }
    
    constraints.push(orderBy('createdAt', 'desc'));
    
    if (constraints.length > 0) {
      q = query(q, ...constraints);
    }
    
    const snapshot = await getDocs(q);
    const pos = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

    return { success: true, data: pos };
  } catch (error) {
    console.error('Error getting purchase orders:', error);
    return { success: false, error: error.message, data: [] };
  }
};

/**
 * Get purchase order by ID
 */
export const getPurchaseOrderById = async (poId) => {
  try {
    const poDoc = await getDoc(doc(db, PO_COLLECTION, poId));
    
    if (!poDoc.exists()) {
      return { success: false, error: 'Purchase order not found' };
    }

    return { 
      success: true, 
      data: { id: poDoc.id, ...poDoc.data() } 
    };
  } catch (error) {
    console.error('Error getting purchase order:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Update purchase order
 */
export const updatePurchaseOrder = async (poId, updates, currentUser) => {
  try {
    const poRef = doc(db, PO_COLLECTION, poId);
    
    await updateDoc(poRef, {
      ...updates,
      updatedAt: Timestamp.now()
    });

    await logActivity({
      userId: currentUser?.uid,
      userEmail: currentUser?.email,
      userName: currentUser?.displayName || currentUser?.email,
      action: ACTIONS.UPDATED_PURCHASE_ORDER,
      entityType: ENTITY_TYPES.PURCHASE_ORDER,
      entityId: poId,
      entityName: `PO #${updates.poNumber || poId}`,
      details: { poNumber: updates.poNumber || poId }
    });

    return { success: true };
  } catch (error) {
    console.error('Error updating purchase order:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Link GRN to Purchase Order
 */
export const linkGRNtoPO = async (poId, grnData, currentUser) => {
  try {
    const poRef = doc(db, PO_COLLECTION, poId);
    const poDoc = await getDoc(poRef);
    
    if (!poDoc.exists()) {
      return { success: false, error: 'Purchase order not found' };
    }

    const poData = poDoc.data();
    const currentGRNs = poData.grns || [];
    
    // Add new GRN
    currentGRNs.push({
      grnId: grnData.grnId,
      grnNumber: grnData.grnNumber,
      receivedDate: Timestamp.now(),
      receivedQuantity: grnData.receivedQuantity
    });

    // Calculate total received quantity
    const totalOrdered = poData.items.reduce((sum, item) => sum + item.quantity, 0);
    const totalReceived = currentGRNs.reduce((sum, grn) => sum + grn.receivedQuantity, 0);

    // Update status
    let newStatus = 'pending';
    if (totalReceived >= totalOrdered) {
      newStatus = 'completed';
    } else if (totalReceived > 0) {
      newStatus = 'partially_received';
    }

    await updateDoc(poRef, {
      grns: currentGRNs,
      status: newStatus,
      updatedAt: Timestamp.now()
    });

    await logActivity({
      userId: currentUser?.uid,
      userEmail: currentUser?.email,
      userName: currentUser?.displayName || currentUser?.email,
      action: ACTIONS.UPDATED_PURCHASE_ORDER,
      entityType: ENTITY_TYPES.PURCHASE_ORDER,
      entityId: poId,
      entityName: `PO #${poData.poNumber} — GRN ${grnData.grnNumber}`,
      details: { poNumber: poData.poNumber, grnNumber: grnData.grnNumber }
    });

    return { success: true };
  } catch (error) {
    console.error('Error linking GRN to PO:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Cancel purchase order
 */
export const cancelPurchaseOrder = async (poId, currentUser) => {
  try {
    const poRef = doc(db, PO_COLLECTION, poId);
    const poDoc = await getDoc(poRef);
    
    if (!poDoc.exists()) {
      return { success: false, error: 'Purchase order not found' };
    }

    const poData = poDoc.data();
    
    // Check if any GRNs are linked
    if (poData.grns && poData.grns.length > 0) {
      return { 
        success: false, 
        error: 'Cannot cancel PO with linked GRNs. Delete GRNs first.' 
      };
    }

    await updateDoc(poRef, {
      status: 'cancelled',
      updatedAt: Timestamp.now()
    });

    await logActivity({
      userId: currentUser?.uid,
      userEmail: currentUser?.email,
      userName: currentUser?.displayName || currentUser?.email,
      action: ACTIONS.CANCELLED_PURCHASE_ORDER,
      entityType: ENTITY_TYPES.PURCHASE_ORDER,
      entityId: poId,
      entityName: `PO #${poData.poNumber}`,
      details: { poNumber: poData.poNumber, supplierName: poData.supplierName }
    });

    return { success: true };
  } catch (error) {
    console.error('Error cancelling purchase order:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Delete purchase order
 */
export const deletePurchaseOrder = async (poId, currentUser) => {
  try {
    const poRef = doc(db, PO_COLLECTION, poId);
    const poDoc = await getDoc(poRef);
    
    if (!poDoc.exists()) {
      return { success: false, error: 'Purchase order not found' };
    }

    const poData = poDoc.data();
    
    // Check if any GRNs are linked
    if (poData.grns && poData.grns.length > 0) {
      return { 
        success: false, 
        error: 'Cannot delete PO with linked GRNs. Delete GRNs first.' 
      };
    }

    await deleteDoc(poRef);

    await logActivity({
      userId: currentUser?.uid,
      userEmail: currentUser?.email,
      userName: currentUser?.displayName || currentUser?.email,
      action: ACTIONS.DELETED_PURCHASE_ORDER,
      entityType: ENTITY_TYPES.PURCHASE_ORDER,
      entityId: poId,
      entityName: `PO #${poData.poNumber}`,
      details: { poNumber: poData.poNumber, supplierName: poData.supplierName }
    });

    return { success: true };
  } catch (error) {
    console.error('Error deleting purchase order:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get pending purchase orders (for GRN linking)
 */
export const getPendingPurchaseOrders = async (supplierId = null) => {
  try {
    // Fetch all then filter in memory to avoid composite index requirement
    const snapshot = await getDocs(query(collection(db, PO_COLLECTION), orderBy('createdAt', 'desc')));
    let pos = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

    pos = pos.filter(po => po.status === 'pending' || po.status === 'partially_received');
    if (supplierId) pos = pos.filter(po => po.supplierId === supplierId);

    return { success: true, data: pos };
  } catch (error) {
    console.error('Error getting pending purchase orders:', error);
    return { success: false, error: error.message, data: [] };
  }
};

/**
 * Get purchase order statistics
 */
export const getPurchaseOrderStats = async () => {
  try {
    const posSnapshot = await getDocs(collection(db, PO_COLLECTION));
    
    let totalPOs = 0;
    let pendingPOs = 0;
    let completedPOs = 0;
    let totalValue = 0;

    posSnapshot.docs.forEach(doc => {
      const data = doc.data();
      totalPOs++;
      totalValue += data.totalAmount || 0;
      
      if (data.status === 'pending' || data.status === 'partially_received') {
        pendingPOs++;
      } else if (data.status === 'completed') {
        completedPOs++;
      }
    });

    return {
      success: true,
      data: {
        totalPOs,
        pendingPOs,
        completedPOs,
        totalValue
      }
    };
  } catch (error) {
    console.error('Error getting PO stats:', error);
    return { success: false, error: error.message };
  }
};
